import { Menu, Transition } from "@headlessui/react";
import type { IconName } from "./Icon";
import Icon from "./Icon";
import { createPortal } from "react-dom";

export interface SubMenuItem {
  id: string;
  text: string;
  icon?: IconName | (() => JSX.Element);
}

// Minimal subset of DOMRect the submenu needs to position itself. Measured by
// the parent (on hover, when the trigger node is guaranteed mounted) and passed
// down, so this component never reads a ref during render. See note below.
export interface TriggerRect {
  top: number;
  left: number;
  right: number;
}

export interface DoubleDropdownProps {
  subMenuItems: SubMenuItem[];
  onSubMenuClick: (id: string) => void;
  subMenuPosition?: "left" | "right";
  type?: "project" | "projectGroup";
  triggerRect: TriggerRect | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SUBMENU_OFFSET_Y = -5;
const SUBMENU_Z_INDEX = 9999;

const DoubleDropdown = ({
  subMenuItems,
  onSubMenuClick,
  subMenuPosition,
  type = "projectGroup",
  triggerRect,
  onMouseEnter,
  onMouseLeave,
}: DoubleDropdownProps) => {
  // Tailwind w-48 / w-56 in px
  const width = type === "project" ? 192 : 224;

  // The parent measures the trigger element on hover (when the node is mounted)
  // and hands us its rect. We compute the fixed position synchronously from that
  // rect. This avoids the bottom-up commit ordering bug where a child's
  // useLayoutEffect read the parent ref before React attached it -> null rect ->
  // submenu never positioned. It broke in production and was masked in dev by
  // React.StrictMode's double-invoked mount.
  if (!triggerRect) return null;

  const preferred = subMenuPosition ?? "right";
  let left: number;
  if (preferred === "right") {
    left = triggerRect.right;
    if (left + width > window.innerWidth) {
      // would overflow the right edge -> flip to the left of the trigger
      left = triggerRect.left - width;
    }
  } else {
    left = triggerRect.left - width;
    if (left < 0) {
      // would overflow the left edge -> flip to the right of the trigger
      left = triggerRect.right;
    }
  }

  const top = triggerRect.top + SUBMENU_OFFSET_Y;

  const menu = (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        top,
        left,
        zIndex: SUBMENU_Z_INDEX,
      }}
      className={`${
        type === "project" ? "w-48" : "w-56"
      } bg-white border border-gray-200 rounded-md shadow-lg focus:outline-none font-normal`}
    >
      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items>
          {subMenuItems.map((subItem) => (
            <Menu.Item key={subItem.id}>
              <button
                className={`w-full ${
                  type === "project" ? "px-2 pt-1.5 pb-1" : "px-3 py-1.5"
                } text-sm hover:bg-gray-100 flex items-center`}
                onClick={() => onSubMenuClick(subItem.id)}
              >
                {typeof subItem.icon === "string" ? (
                  <Icon icon={subItem.icon} className="h-5 w-5 stroke-green-500 mr-2" />
                ) : typeof subItem.icon === "function" ? (
                  <div className="w-5 h-5 mr-2">{subItem.icon()}</div>
                ) : null}
                {subItem.text}
              </button>
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </div>
  );

  return createPortal(menu, document.body);
};

export default DoubleDropdown;
